import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Share, Plus, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface InstallGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isIOS: boolean;
}

export function InstallGuideModal({ open, onOpenChange, isIOS }: InstallGuideModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm mx-4">
        <DialogHeader>
          <DialogTitle className="text-center">
            {t('pwa.installGuideTitle', 'Install Gemurhood App')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {isIOS ? (
            // iOS install guide (neutral, no browser-specific wording)
            <>
              <div className="text-center text-sm text-muted-foreground mb-4">
                {t('pwa.iosGuideIntro', 'Add to your home screen to use it like an app')}
              </div>

              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Share size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {t('pwa.iosStep1Title', '1. Tap the Share button')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('pwa.iosStep1Desc', 'Tap the Share button at the bottom (or top) of your browser')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Plus size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {t('pwa.iosStep2Title', '2. Add to Home Screen')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('pwa.iosStep2Desc', 'Select "Add to Home Screen" option')}
                  </p>
                </div>
              </div>

              {/* Visual hint */}
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <div className="flex justify-center gap-6 mb-2">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center mb-1">
                      <Share size={16} className="text-primary" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Share</span>
                  </div>
                  <div className="flex items-center text-muted-foreground">→</div>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded bg-primary/20 flex items-center justify-center mb-1">
                      <Plus size={16} className="text-primary" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Add to Home</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Android/Desktop fallback (when native prompt fails)
            <>
              <div className="text-center text-sm text-muted-foreground mb-4">
                {t('pwa.androidGuideIntro', 'You can install the app from your browser menu')}
              </div>

              {/* Step 1 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MoreVertical size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {t('pwa.androidStep1Title', '1. Open Menu')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('pwa.androidStep1Desc', 'Tap the menu (⋮) at the top right of your browser')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Plus size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {t('pwa.androidStep2Title', '2. Install App or Add to Home Screen')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('pwa.androidStep2Desc', 'Select "Install App" or "Add to Home Screen"')}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
